import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, Building2, Crown, FileText, Loader2, Receipt, ShoppingCart } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useGroupOverview, type GroupStore } from '@/hooks/useGroupOverview';
import { useSetActiveCustomer } from '@/hooks/useMemberships';

const fmtMoney = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

/**
 * /stores — the chain / franchise rollup. Users who belong to a group
 * master account (e.g. the KEYSTONE FOODS people over their Starbird
 * stores) see every store here with its live open balance, and jump into
 * any store's ordering or invoices with one click (switches the active
 * account, same as the sidebar switcher).
 */
export default function AllStores() {
  const { data, isLoading } = useGroupOverview();
  const setActive = useSetActiveCustomer();
  const navigate = useNavigate();

  const open = (customerId: string, to: string) => {
    setActive.mutate(customerId, { onSuccess: () => navigate(to) });
  };

  if (isLoading) {
    return (
      <div className="px-6 py-16 text-center text-sm text-brix-muted">
        <Loader2 size={16} className="animate-spin inline-block mr-2" />
        Loading your stores…
      </div>
    );
  }

  const groups = data?.groups ?? [];
  if (groups.length === 0) {
    return (
      <div className="px-6 py-16 text-center text-sm text-brix-muted">
        This account isn't part of a chain or franchise group.
      </div>
    );
  }

  return (
    <div className="px-6 py-6 space-y-8 max-w-5xl">
      {groups.map((g) => {
        const totalOpen = g.stores.reduce((s, x) => s + x.open_balance, 0);
        const totalPastDue = g.stores.reduce((s, x) => s + x.past_due, 0);
        return (
          <section key={g.master.customer_id} className="space-y-4">
            <div>
              <h1 className="font-display text-xl font-semibold text-brix-ink">
                {g.master.group_brand || g.master.name}
                <span className="ml-2 text-sm font-medium text-brix-muted">
                  {g.master.group_kind === 'chain' ? 'Chain' : 'Franchise'} ·{' '}
                  {g.stores.length} account{g.stores.length === 1 ? '' : 's'}
                </span>
              </h1>
              <p className="text-sm text-brix-muted mt-1">
                Combined open balance {fmtMoney(totalOpen)}
                {totalPastDue > 0 && (
                  <span className="text-amber-600 font-semibold">
                    {' '}· {totalPastDue} past-due invoice{totalPastDue === 1 ? '' : 's'}
                  </span>
                )}
              </p>
              {/* The roll-up above answers "how much"; the statement answers
                  "which invoices, at which store" — the document an AP clerk
                  actually works from. */}
              <Link
                to="/statements"
                className="mt-2.5 inline-flex items-center gap-1.5 text-sm font-semibold text-brix-primary hover:underline"
              >
                <FileText size={14} /> Open the combined statement
              </Link>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {g.stores.map((s) => (
                <StoreCard key={s.customer_id} store={s} onOpen={open} busy={setActive.isPending} />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function StoreCard({
  store,
  onOpen,
  busy,
}: {
  store: GroupStore;
  onOpen: (customerId: string, to: string) => void;
  busy: boolean;
}) {
  return (
    <div
      className={cn(
        'rounded-xl border bg-brix-surface px-4 py-3.5 space-y-2.5',
        store.past_due > 0 ? 'border-amber-300' : 'border-brix-border',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-brix-ink truncate inline-flex items-center gap-1.5">
            <Building2 size={14} className="text-brix-primary shrink-0" />
            {store.name}
          </p>
          {store.is_master && (
            <span className="ml-1 inline-flex items-center gap-1 text-[11px] font-semibold text-brix-primary">
              <Crown size={11} /> Master account
            </span>
          )}
        </div>
        {store.past_due > 0 && (
          <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 border border-amber-300 px-2 py-0.5 text-[11px] font-bold">
            <AlertTriangle size={11} /> {store.past_due} past due
          </span>
        )}
      </div>

      <div className="flex items-baseline gap-3 text-[13px]">
        <span className="text-brix-ink font-semibold">{fmtMoney(store.open_balance)} open</span>
        <span className="text-brix-muted">
          {store.open_invoices} open invoice{store.open_invoices === 1 ? '' : 's'}
        </span>
        {store.last_order_at && (
          <span className="text-brix-muted">
            Last order {new Date(store.last_order_at).toLocaleDateString()}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Button size="sm" disabled={busy} onClick={() => onOpen(store.customer_id, '/order')}>
          <ShoppingCart size={13} /> Order
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={busy}
          onClick={() => onOpen(store.customer_id, '/invoices')}
        >
          <Receipt size={13} /> Invoices
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={busy}
          onClick={() => onOpen(store.customer_id, '/home')}
        >
          Open →
        </Button>
      </div>
    </div>
  );
}
